import * as React from 'react';
import * as classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import { observable, computed, action } from 'mobx';
import { unformat, toFixed } from 'accounting';
import * as dateParse from 'date-fns/parse';
import * as dateFormat from 'date-fns/format';
import { PortalSearchItemInventoryPbi, PortalSearchItemInventoryPac } from '.';
import { IAppOnlyStore } from '../../../stores';
import {
    ISearchItem,
    IItemInventoryCount,
    IItemInventory,
    EInventoryStatus,
    IItemPrice
} from '../../../../../common/interfaces/search';
import { IStockDetailsView } from '../../../../../common/interfaces/stock';
import { NoImagePlaceholder, ItemThumb } from '../../../components/item';
import { DeleteConfirmation } from '../../../components/popup';
import { CustomElement } from '../../../components/element';
import { StringMethod } from '../../../../../common/helpers/string';
import AnimateHeight from 'react-animate-height';

// Styles
const s = require('./PortalSearchItem.css');
const plusGreyIcon = require('./../../../assets/plus-grey-icon.svg');
const plusBlueIcon = require('./../../../assets/plus-blue-icon.svg');
const minusBlueIcon = require('./../../../assets/minus-blue-icon.svg');
const trashCanIcon = require('./../../../assets/trash-can-icon.svg');

// Props
interface ISearchProps extends IAppOnlyStore {
    id: number;
    item: ISearchItem;
    isViewedFromCart?: boolean;
}

// Component
@inject('store')
@observer
export class PortalSearchItem extends React.Component<ISearchProps, {}> {
    static get defaultProps() {
        return {
            id: 0,
            item: null,
            isViewedFromCart: false
        };
    }

    private outOfStockLabel: string = 'Out of stock';

    @observable private showDeleteDialog: boolean = false;

    @computed
    private get itemNameFormatted(): { name: string; longestWord: number } {
        const formattedItemName = StringMethod.removeUnwantedCharactersFromEnd(this.props.item.itemName, [',', ';', ':']);

        const longestWord = StringMethod.countTheLongestWord(formattedItemName);

        return {
            name: formattedItemName,
            longestWord: longestWord
        };
    }

    @computed
    private get itemInventory(): IItemInventory[] {
        const {
            item,
            store: { user }
        } = this.props;
        const inventory: IItemInventory[] = [];

        if (user.isCustomer) {
            if (user.customerIsPAC) {
                if (user.customer.whsName || user.customer.whscode) {
                    const localStock = this.stockCountForPAC(item.LocalStock);
                    inventory.push({
                        code: user.customer.whsName || user.customer.whscode,
                        numberCount: localStock.numberCount,
                        count: localStock.count,
                        status: localStock.status
                    });
                }

                const regionalStock = this.stockCountForPAC(item.RegionalStock);
                inventory.push({
                    code: 'Regional',
                    numberCount: regionalStock.numberCount,
                    count: regionalStock.count,
                    status: regionalStock.status
                });
            } else if (user.customerIsPBI) {
                let californiaStock = { code: 'CA', ...this.stockCountForPBI(item.stockCA) };
                let texasStock = { code: 'TX', ...this.stockCountForPBI(item.stockTX) };
                let virginiaStock = { code: 'VA', ...this.stockCountForPBI(item.stockVA) };
                const combined = [californiaStock, texasStock, virginiaStock];

                combined.map((stock) => {
                    inventory.push({
                        code: stock.code,
                        numberCount: stock.numberCount,
                        count: stock.count !== null ? stock.count : null,
                        status: stock.status !== null ? stock.status : null
                    });
                });
            }
        }

        return inventory;
    }

    private stockCountForPAC = (count: string | number): IItemInventoryCount => {
        count = typeof count === 'string' ? unformat(count) : count;

        if (count >= 3) {
            return {
                numberCount: count,
                count: 'In stock',
                status: EInventoryStatus.JustCount
            };
        } else if (count < 3 && count >= 1) {
            return {
                numberCount: count,
                count: 'Limited qty',
                status: EInventoryStatus.JustCount
            };
        } else {
            return {
                numberCount: count,
                count: this.outOfStockLabel,
                status: EInventoryStatus.JustCount
            };
        }
    };

    private stockCountForPBI = (count: string | number): IItemInventoryCount => {
        count = typeof count === 'string' ? unformat(count) : count;

        if (count >= 10) {
            return {
                numberCount: count,
                count: 10,
                status: EInventoryStatus.LargerOrEqual
            };
        } else if (count < 10 && count >= 1) {
            return {
                numberCount: count,
                count: 10,
                status: EInventoryStatus.SmallerThan
            };
        } else {
            return {
                numberCount: count,
                count: this.outOfStockLabel,
                status: EInventoryStatus.Unavailable
            };
        }
    };

    @computed
    private get isItemOutOfStock(): boolean {
        const otherStockFound = this.itemInventory.some((inventory) => {
            return inventory.count !== this.outOfStockLabel;
        });

        return !otherStockFound;
    }

    @computed
    private get itemETA(): Date | null {
        if (this.props.item.docduedate) {
            return dateParse(this.props.item.docduedate);
        }

        return null;
    }

    @computed
    private get itemQuantityInCurrentCart(): number {
        const {
            item,
            store: { cart }
        } = this.props;

        const itemFound = cart.currentCartItemIds.indexOf(item.id);
        return itemFound !== -1 ? cart.currentCart.item[itemFound].quantity : 0;
    }

    @computed
    private get itemShowListPrice(): boolean {
        const {
            item,
            store: { user }
        } = this.props;

        return user.isCustomer && user.customerListNumber !== 11 && item.listPrice !== null && unformat(item.listPrice) > 0;
    }

    @computed
    private get itemListPrice(): number | null {
        const { item } = this.props;

        if (typeof item.listPrice !== 'undefined' && item.listPrice !== null) {
            return typeof item.listPrice === 'number'
                ? item.listPrice
                : typeof item.listPrice === 'string' && item.listPrice.trim() !== ''
                ? unformat(item.listPrice)
                : null;
        } else {
            return null;
        }
    }

    @computed
    private get itemPrice(): number | null {
        const { item } = this.props;

        if (typeof item.price !== 'undefined' && item.price !== null) {
            return typeof item.price === 'number'
                ? item.price
                : typeof item.price === 'string' && item.price.trim() !== ''
                ? unformat(item.price)
                : null;
        } else {
            return null;
        }
    }

    // If item price is null or free and list price is not shown, ask client for a call
    @computed
    private get itemPriceRequiresACall(): boolean {
        return (this.itemPrice === null || this.itemPrice === 0) && this.itemShowListPrice === false;
    }

    // For cooling items hide item price if:
    // - customer is on price list = 7
    // - customer price is equal to or higher than 1000
    @computed
    private get itemPriceHiddenDueToCoolingLimitation(): boolean {
        return this.props.store.user.customerIsOnPriceListNumSeven &&
            this.props.item.itemClass &&
            this.props.item.itemClass.toUpperCase() === 'COOLING'
            ? this.itemPrice && this.itemPrice >= 1000
            : false;
    }

    @computed
    private get itemPrices(): IItemPrice[] {
        const { item } = this.props;
        const prices: IItemPrice[] = [];

        if (this.itemPriceRequiresACall || this.itemPriceHiddenDueToCoolingLimitation) {
            prices.push({
                label: 'N/A',
                opensWarehousesLocations: true,
                currencySymbol: null,
                beforeDecimal: null,
                afterDecimal: null
            });
        } else {
            if (this.itemPrice !== null && this.itemPrice > 0) {
                const itemCustomerPrice = toFixed(this.itemPrice, 2).split('.');
                prices.push({
                    label: 'Your price',
                    opensWarehousesLocations: false,
                    currencySymbol: '$',
                    beforeDecimal: itemCustomerPrice[0],
                    afterDecimal: itemCustomerPrice[1]
                });
            }

            if (this.itemShowListPrice) {
                const itemListPrice = toFixed(unformat(item.listPrice), 2).split('.');
                prices.push({
                    label: 'List price',
                    opensWarehousesLocations: false,
                    currencySymbol: '$',
                    beforeDecimal: itemListPrice[0],
                    afterDecimal: itemListPrice[1]
                });
            }
        }

        return prices;
    }

    @computed
    private get showFitments(): boolean {
        const { item } = this.props;
        return (
            typeof item.showFitments !== 'undefined' &&
            typeof item.fitments !== 'undefined' &&
            item.fitments.length > 1 &&
            item.showFitments
        );
    }

    @computed
    private get showStockDetails(): boolean {
        return this.props.store.user.isCustomer && this.itemPriceHiddenDueToCoolingLimitation === false;
    }

    @computed
    private get stockDetails(): IStockDetailsView | null {
        const { user, stock } = this.props.store;

        const itemIsShown = stock.stockDetailsShownItemCodes.indexOf(this.props.item.itemCode);
        if (user.isCustomer && itemIsShown !== -1) {
            const itemStockIndex = stock.stockDetailsItemCodes.indexOf(this.props.item.itemCode);
            return itemStockIndex !== -1
                ? {
                      loading: stock.stockDetailsShown[itemIsShown].loading,
                      warehouses: stock.stockDetails[itemStockIndex].warehouses
                  }
                : stock.stockDetailsShown[itemIsShown].loading
                ? {
                      loading: true,
                      warehouses: []
                  }
                : null;
        } else {
            return null;
        }
    }

    public render() {
        const {
            item,
            store: { user }
        } = this.props;

        return (
            <div className={s.itemContainer}>
                <div className={s.item}>
                    <div className={s.itemDetails}>
                        <div className={s.itemImg}>
                            {((item.imageThumb || item.image) && (
                                <ItemThumb
                                    image={item.image}
                                    imageThumb={item.imageThumb}
                                    onThumbClick={this.handleItemThumbnailClick}
                                />
                            )) || <NoImagePlaceholder />}
                        </div>
                        <div className={s.itemCodeName}>
                            <h4>
                                <img src={plusGreyIcon} alt="" />
                                <strong>{item.itemCode}</strong>
                                {item.custItemCode && <span>/</span>}
                                {item.custItemCode && <em>{item.custItemCode}</em>}
                            </h4>
                            <p className={this.itemNameFormatted.longestWord > 32 ? s.breakLongItemName : undefined}>
                                <span>{this.itemNameFormatted.name}</span>
                            </p>
                            {typeof item.showFitments !== 'undefined' && item.fitments.length > 1 && (
                                <button onClick={this.handleToggleFitments}>
                                    {(item.showFitments ? 'Hide' : 'Show') + ' fitments'}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={s.partDetails}>
                        <div>
                            <p>
                                OEM: <strong>{item.oem || 'N/A'}</strong>
                            </p>
                            <p>
                                PARTSLINK: <strong>{item.Partslink || 'N/A'}</strong>
                            </p>
                        </div>
                        <div>
                            <p>
                                DPI: <strong>{item.dpi || 'N/A'}</strong>
                            </p>
                            <p>
                                CERT: <strong>{item.certification || 'N/A'}</strong>
                            </p>
                        </div>
                    </div>

                    {(this.showStockDetails && user.customerIsPBI && (
                        <PortalSearchItemInventoryPbi
                            customerIsPBIPlus={user.customerIsPBIPlus}
                            itemcode={item.itemCode}
                            inventory={this.itemInventory}
                            californiaStockDetails={this.stockDetails}
                            onCaliforniaStockDetailsShow={this.handleStockDetailsOpenClick}
                            onCaliforniaStockDetailsHide={this.handleStockDetailsHideClick}
                        />
                    )) ||
                        (this.showStockDetails && user.customerIsPAC && (
                            <PortalSearchItemInventoryPac
                                itemcode={item.itemCode}
                                inventory={this.itemInventory}
                                regionalStockDetails={this.stockDetails}
                                onRegionalStockDetailsShow={this.handleStockDetailsOpenClick}
                                onRegionalStockDetailsHide={this.handleStockDetailsHideClick}
                            />
                        )) || <div className={s.inventory} />}

                    {user.permissionId >= 2 && (
                        <div className={s.price}>
                            {this.itemPrices.map((price) => (
                                <CustomElement
                                    key={price.label}
                                    component={price.opensWarehousesLocations ? 'button' : 'p'}
                                    properties={{
                                        className: classNames(s.priceEntry, {
                                            [s.priceEntryButton]: price.opensWarehousesLocations
                                        }),
                                        ...(price.opensWarehousesLocations
                                            ? {
                                                  onClick: this.handleOpeningWarehousesLocation
                                              }
                                            : {})
                                    }}
                                >
                                    <em>{price.label}</em>
                                    {price.currencySymbol !== null && <span>{price.currencySymbol}</span>}
                                    {price.beforeDecimal && <strong>{price.beforeDecimal}</strong>}
                                    {price.beforeDecimal && price.afterDecimal && <span>.{price.afterDecimal}</span>}
                                </CustomElement>
                            ))}
                        </div>
                    )}

                    {(user.permissionId >= 3 && this.isItemOutOfStock && this.props.isViewedFromCart === false && (
                        <div className={s.order}>
                            <button
                                className={classNames(s.priceEntry, s.priceEntryButton, s.itemOutOfStock)}
                                onClick={this.handleOpeningWarehousesLocation}
                            >
                                <span>Item is out of stock</span>
                                <strong>
                                    {this.itemETA ? 'ETA: ' + dateFormat(this.itemETA, 'MM/DD/YYYY') : 'Please call for ETA'}
                                </strong>
                            </button>
                        </div>
                    )) ||
                        (user.permissionId >= 3 && this.itemPriceRequiresACall && (
                            <div className={s.order}>
                                <button
                                    className={classNames(s.priceEntry, s.priceEntryButton, s.itemOutOfStock)}
                                    onClick={this.handleOpeningWarehousesLocation}
                                >
                                    <span>Item price is unknown</span>
                                    <strong>Please call for pricing</strong>
                                </button>
                            </div>
                        )) ||
                        (user.permissionId >= 3 && this.itemPriceHiddenDueToCoolingLimitation && (
                            <div className={s.order}>
                                <button
                                    className={classNames(s.priceEntry, s.priceEntryButton, s.itemOutOfStock)}
                                    onClick={this.handleOpeningWarehousesLocation}
                                >
                                    <span>Item stock not available</span>
                                    <strong>Please call or email for stock check</strong>
                                </button>
                            </div>
                        )) ||
                        (user.permissionId >= 3 && (
                            <div className={s.order}>
                                {(this.itemQuantityInCurrentCart > 0 || this.props.isViewedFromCart) && (
                                    <button
                                        className={s.removeFromCart}
                                        title="Remove item from order"
                                        onClick={this.handleOpeningRemoveFromCartDialog}
                                    >
                                        <img src={trashCanIcon} alt="" />
                                    </button>
                                )}

                                <div className={s.inCartCounter}>
                                    <input
                                        type="text"
                                        placeholder="0"
                                        value={this.itemQuantityInCurrentCart || ''}
                                        onChange={this.handleChangeCountInput}
                                    />
                                    <div className={s.increaseDecrease}>
                                        <button onClick={this.handleItemInCartQuantityIncreaseOrDecrease(true)}>
                                            <img src={plusBlueIcon} alt="" />
                                        </button>
                                        <button onClick={this.handleItemInCartQuantityIncreaseOrDecrease(false)}>
                                            <img src={minusBlueIcon} alt="" />
                                        </button>
                                    </div>
                                </div>

                                <DeleteConfirmation
                                    show={this.showDeleteDialog}
                                    cancelLabel="Cancel"
                                    confirmationLabel={`Remove item ${item.itemCode} from cart}`}
                                    onCancel={this.handleDimissalOfRemoveFromCartDialog}
                                    onConfirmDeletion={this.handleRemovalOfItemFromCart}
                                />
                            </div>
                        ))}
                </div>

                <AnimateHeight duration={500} height={this.showFitments ? 'auto' : 0}>
                    <div className={s.fitments}>
                        <div className={s.fitmentsHeader}>
                            <p>
                                The part <strong>{item.itemCode}</strong> results in multiple applications:
                            </p>
                        </div>
                        <div className={s.fitmentsResults}>
                            {typeof item.fitments !== 'undefined' &&
                                item.fitments.map((fitment) => {
                                    return fitment === null ? null : (
                                        <div
                                            key={
                                                'fitment-' +
                                                item.id +
                                                '-' +
                                                fitment.fromYear +
                                                '-' +
                                                fitment.toYear +
                                                '-' +
                                                fitment.make +
                                                '-' +
                                                fitment.model +
                                                (fitment.subModels ? '-' + fitment.subModels : '') +
                                                (fitment.engineSpec ? '-' + fitment.engineSpec : '') +
                                                (fitment.oem ? '-' + fitment.oem : '') +
                                                (fitment.transType ? '-' + fitment.transType : '') +
                                                (fitment.variables ? '-' + fitment.variables : '')
                                            }
                                            className={s.fitment}
                                        >
                                            <p className={s.fitmentCar}>
                                                <strong>
                                                    {fitment.fromYear +
                                                        ' - ' +
                                                        fitment.toYear +
                                                        ' ' +
                                                        fitment.make +
                                                        ' ' +
                                                        fitment.model}
                                                </strong>

                                                {fitment.subModels && <span>Sub models</span>}
                                                {fitment.subModels && <em>{fitment.subModels}</em>}

                                                {fitment.engineSpec && <span>Engine</span>}
                                                {fitment.engineSpec && <em>{fitment.engineSpec}</em>}

                                                {fitment.transType && <span>Transmission</span>}
                                                {fitment.transType && <em>{fitment.transType}</em>}

                                                {fitment.variables && <span>Other</span>}
                                                {fitment.variables && <em>{fitment.variables}</em>}
                                            </p>
                                        </div>
                                    );
                                })}
                        </div>
                        <div className={s.fitmentsFooter}>
                            <div>
                                <button onClick={this.handleToggleFitments}>Hide fitments</button>
                            </div>
                        </div>
                        <br />
                    </div>
                </AnimateHeight>
            </div>
        );
    }

    @action
    private handleItemThumbnailClick = (image: string | null, imageThumb: string | null) => {
        this.props.store.gallery.showImage({
            full: image,
            thumb: imageThumb
        });
    };

    private handleStockDetailsOpenClick = () => {
        if (this.stockDetails === null) {
            this.props.store.stock.showStockDetails(this.props.item.itemCode);
        }
    };

    private handleStockDetailsHideClick = () => {
        this.props.store.stock.hideStockDetails(this.props.item.itemCode);
    };

    private handleItemInCartQuantityIncreaseOrDecrease = (increase: boolean = true) => (e: React.MouseEvent<HTMLElement>) => {
            this.props.store.cart.changeCartItemCount(
                this.props.item,
                increase
                    ? this.itemQuantityInCurrentCart + 1
                    : this.itemQuantityInCurrentCart > 1
                    ? this.itemQuantityInCurrentCart - 1
                    : 0
            );
        };

    private handleChangeCountInput = (e: React.FormEvent<HTMLInputElement>) => {
        this.props.store.cart.changeCartItemCount(this.props.item, unformat(e.currentTarget.value));
    };

    private handleToggleFitments = () => {
        const { item, store } = this.props;
        store.search.toggleFitments(item.id);
    };

    @action
    private handleOpeningWarehousesLocation = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.user.showUserDropdown = false;
        this.props.store.warehouse.showWarehousesLocations = true;
    };

    private handleOpeningRemoveFromCartDialog = () => {
        this.showDeleteDialog = true;
    };

    private handleDimissalOfRemoveFromCartDialog = () => {
        this.showDeleteDialog = false;
    };

    private handleRemovalOfItemFromCart = () => {
        this.props.store.cart.changeCartItemCount(this.props.item, 0, true);
        this.showDeleteDialog = false;
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/portal/PortalSearch/PortalSearchItem.tsx